import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextField, Tooltip, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";
import { getData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";

export const SendSmsEditModal = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const currentConfig = data?.data?.config || {};
  const [body, setBody] = useState(currentConfig.body || "");
  const [fromNumberId, setFromNumberId] = useState(currentConfig.fromNumberId || "");
  const [toNumberSource, setToNumberSource] = useState(currentConfig.toNumberSource || "contact");
  const [toNumberVariable, setToNumberVariable] = useState(currentConfig.toNumberVariable || "");
  const [toNumberLiteral, setToNumberLiteral] = useState(currentConfig.toNumberLiteral || "");

  const [numbers, setNumbers] = useState([]);

  useEffect(() => {
    if (!open) return;
    getData(ENDPOINTS.CALLS.NUMBERS)
      .then((numData) => {
        const numList = Array.isArray(numData) ? numData : numData?.Content || [];
        setNumbers(numList);
      })
      .catch(() => setNumbers([]));
  }, [open]);

  useEffect(() => {
    if (data?.data?.config) {
      const cfg = data.data.config;
      setBody(cfg.body || "");
      setFromNumberId(cfg.fromNumberId || "");
      setToNumberSource(cfg.toNumberSource || "contact");
      setToNumberVariable(cfg.toNumberVariable || "");
      setToNumberLiteral(cfg.toNumberLiteral || "");
    }
  }, [data]);

  const insertVariable = (tag) => {
    setBody((prev) => prev + ` {{${tag}}} `);
  };

  const onSave = () => {
    const config = {
      body: body.trim(),
      fromNumberId: fromNumberId ? Number(fromNumberId) : null,
      toNumberSource,
      toNumberVariable: toNumberSource === "variable" ? toNumberVariable : undefined,
      toNumberLiteral: toNumberSource === "literal" ? toNumberLiteral : undefined,
    };
    handleClose({ config, bodySnippet: body.slice(0, 60) }, isEdit);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500">
            <SmsIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit Send SMS Node" : "Configure Send SMS"}
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Sends an automated SMS message via Twilio or carrier integration.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          <FormControl fullWidth size="small">
            <InputLabel id="sms-number-label">Sender Number (Optional)</InputLabel>
            <Select
              labelId="sms-number-label"
              value={fromNumberId}
              label="Sender Number (Optional)"
              onChange={(e) => setFromNumberId(e.target.value)}
            >
              <MenuItem value="">Default Sender Number</MenuItem>
              {numbers.map((num) => (
                <MenuItem key={num.id} value={num.id}>
                  {num.friendly_name ? `${num.friendly_name} (${num.phone_number})` : num.phone_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="sms-target-label">Target Phone Number</InputLabel>
            <Select
              labelId="sms-target-label"
              value={toNumberSource}
              label="Target Phone Number"
              onChange={(e) => setToNumberSource(e.target.value)}
            >
              <MenuItem value="contact">Enrolled Contact Phone (Default)</MenuItem>
              <MenuItem value="variable">Context Variable</MenuItem>
              <MenuItem value="literal">Literal Number</MenuItem>
            </Select>
          </FormControl>

          {toNumberSource === "variable" && (
            <TextField
              fullWidth
              size="small"
              label="Variable Path"
              value={toNumberVariable}
              onChange={(e) => setToNumberVariable(e.target.value)}
              placeholder="context.phone"
            />
          )}

          {toNumberSource === "literal" && (
            <TextField
              fullWidth
              size="small"
              label="E.164 Phone Number"
              value={toNumberLiteral}
              onChange={(e) => setToNumberLiteral(e.target.value)}
              placeholder="+1234567890"
            />
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`text-xs font-semibold ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
                SMS Message Body *
              </label>
              <span className={`text-[11px] ${darkMode ? "text-zinc-500" : "text-slate-400"}`}>
                {body.length} characters
              </span>
            </div>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{contact.first_name}}, thanks for your call. Your appointment is confirmed..."
            />
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className={`text-[11px] font-medium mr-1 ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
                Insert Tag:
              </span>
              {["contact.first_name", "contact.Name", "contact.phone", "call.duration", "context.appointment_date"].map((tag) => (
                <Chip
                  key={tag}
                  label={`{{${tag}}}`}
                  size="small"
                  clickable
                  onClick={() => insertVariable(tag)}
                  style={{ fontSize: 10, height: 20 }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!body.trim()}
            style={{ backgroundColor: "#10b981" }}
          >
            {isEdit ? "Update Node" : "Add Node"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const SendSmsNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const cfg = data?.config || {};
  const bodyText = cfg.body || data?.bodySnippet || "No message body set";

  const branches = [
    { key: "delivered", label: "Delivered", color: "bg-emerald-500" },
    { key: "undelivered", label: "Undelivered", color: "bg-amber-500" },
    { key: "failed", label: "Failed", color: "bg-rose-500" },
    { key: "next", label: "Next", color: "bg-slate-400" },
  ];

  const isBranchConnected = (branchKey) =>
    edges.some((e) => e.source === rest.id && (e.sourceHandle === branchKey || (!e.sourceHandle && branchKey === "next")));

  return (
    <div
      className={`w-60 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      } ${
        selected ? "ring-2 ring-emerald-500 shadow-emerald-500/20" : "hover:border-emerald-400"
      }`}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="flex items-center gap-1.5 min-w-0">
          <SmsIcon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">
            Send SMS
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Node">
            <EditIcon
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => onEdit(props)}
            />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2.5">
        <div className={`text-xs line-clamp-2 ${darkMode ? "text-zinc-200" : "text-slate-700"}`}>
          {bodyText}
        </div>
        <div className={`text-[10px] mt-1.5 ${darkMode ? "text-zinc-400" : "text-slate-400"}`}>
          To: {cfg.toNumberSource || "contact"}
        </div>
      </div>

      <div className={`border-t ${darkMode ? "border-zinc-800 bg-zinc-900/50" : "border-slate-100 bg-slate-50/50"} px-2.5 py-2 space-y-1.5`}>
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1">
          Delivery Status
        </div>
        {branches.map((b) => {
          const connected = isBranchConnected(b.key);
          return (
            <div key={b.key} className="flex items-center justify-between relative py-0.5">
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${b.color}`} />
                <span className={`text-[11px] font-medium ${darkMode ? "text-zinc-300" : "text-slate-600"}`}>
                  {b.label}
                </span>
              </div>
              <HandleDot
                addVisible={!connected}
                id={b.key}
                type="source"
                position={Position.Right}
                onAdd={(v) =>
                  onAdd(v, rest, rest.id, {
                    type: "SEND_SMS",
                    key: b.key,
                  })
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

